import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Rating,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Instagram as InstagramIcon,
  Settings as SettingsIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { providersApi } from '../services/api';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import ProviderStatusChips from './ProviderStatusChips';
import MobileProOnboardingToggle from './MobileProOnboardingToggle';
import CountryCityFilter from './CountryCityFilter';
import { useApi } from '../contexts/ApiContext';

const SERVICE_TYPES = ['DOG_WALKING', 'BOARDING', 'HOUSE_SITTING', 'DROP_IN_VISITS', 'DAY_CARE', 'TRAINING', 'GROOMING', 'STORE'];
const PROVIDER_TYPES = ['SERVICE', 'INFLUENCER', 'STORE'];
const PROVIDER_STATES = ['SUBMITTED', 'APPROVED', 'DECLINED'];
const INTAKE_SOURCES = ['SEEDED', 'PORTAL', 'MOBILE'];
const VISIBILITIES = ['VISIBLE', 'HIDDEN', 'SUSPENDED'];

/** Column key -> header label. The key is what ProviderSortKeys allowlists server-side. */
const SORTABLE = [
  { key: 'id', label: 'ID' },
  { key: 'title', label: 'Provider' },
  { key: 'user', label: 'User' },
  { key: 'intakeSource', label: 'Origin' },
  { key: 'status', label: 'Status' },
  { key: 'rating', label: 'Rating' },
  { key: 'services', label: 'Services' },
  { key: 'createdAt', label: 'Created' },
];

const Providers = () => {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [currentProvider, setCurrentProvider] = useState(null);
  const [formData, setFormData] = useState({ title: '', about: '', instagramUser: '', documentUrl: '', services: '', images: '' });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState(null);
  const [totalElements, setTotalElements] = useState(0);

  const navigate = useNavigate();
  const api = useApi();
  const [searchParams, setSearchParams] = useSearchParams();

  // The URL is the single source of truth for what is being listed, so a view can be
  // bookmarked, shared, and survives Back after opening a provider.
  const sort = searchParams.get('sort') || 'createdAt';
  // Upper-cased on read: the backend uses Sort.Direction.fromOptionalString precisely so a
  // shared `?direction=asc` does not 400, so comparing the raw value here would make the header
  // arrow disagree with the actual order and the column appear not to toggle.
  const direction = (searchParams.get('direction') || 'DESC').toUpperCase();
  // Number('abc') is NaN, which would flow straight into `?page=NaN` and a 400 the user can
  // only escape by editing the URL -- and hand-editable URLs are the point of this change.
  const rawPage = Number(searchParams.get('page'));
  const page = Number.isFinite(rawPage) && rawPage >= 0 ? Math.floor(rawPage) : 0;
  // In the URL too. With page shared but size local, sending someone `?page=3` after setting
  // 100 rows lands them on rows 60-79 instead of 300-399.
  const rawSize = Number(searchParams.get('size'));
  const rowsPerPage = [5, 10, 20, 50, 100].includes(rawSize) ? rawSize : 20;

  const filters = useMemo(() => ({
    name: searchParams.get('name') || '',
    countryId: searchParams.get('countryId') || '',
    cityId: searchParams.get('cityId') || '',
    state: searchParams.get('state') || '',
    serviceType: searchParams.get('serviceType') || '',
    providerType: searchParams.get('providerType') || '',
    claimed: searchParams.get('claimed') || '',
    intakeSource: searchParams.get('intakeSource') || '',
    visibility: searchParams.get('visibility') || '',
  }), [searchParams]);

  /** One writer for every URL-backed control. A null or '' value removes the param. */
  const setParams = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === '') next.delete(k); else next.set(k, v);
    });
    // Any change to WHAT is listed invalidates the current page number.
    if (!('page' in patch)) next.delete('page');
    // replace, not push: the goal is a URL you can copy, not a history entry per keystroke of
    // filter tweaking. Without this, setting Origin + Visibility + a sort means four Backs to
    // leave the page.
    setSearchParams(next, { replace: true });
  };

  // The free-text Name field keeps a pending buffer and a Search button. Wiring them straight
  // to setParams would put a URL write inside fetchProviders' dependency chain and fire one
  // request per keystroke -- that split is what pendingFilters was for, and it is the half
  // worth keeping. The selects write immediately.
  const [pendingText, setPendingText] = useState({ name: '' });
  useEffect(() => {
    setPendingText({ name: filters.name });
  }, [filters.name]);
  const applyText = () => setParams(pendingText);

  // Pipeline counts: the two stages that have no provider row yet, so the list below can never
  // show them.
  // Counted SEPARATELY, because each links to its own Submissions tab. A single summed count
  // would say "5 awaiting review" and land on a list of 2.
  const [mobileReview, setMobileReview] = useState(0);
  const [portalReview, setPortalReview] = useState(0);
  const [awaitingClaim, setAwaitingClaim] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api.moderation.getCounts()
      .then((c) => {
        if (cancelled) return;
        setMobileReview(c?.providers || 0);
        setPortalReview(c?.publicProviderApplications || 0);
      })
      .catch(() => { /* the strip is an affordance, not the page -- stay silent on failure */ });

    // size=1 deliberately: only totalElements is wanted, and the admin application DTO hydrates
    // a LAZY matchedUser plus a findByUserId PER ROW.
    api.providerApplication.list({ page: 0, size: 1, status: 'APPROVED' })
      .then((r) => { if (!cancelled) setAwaitingClaim(r?.totalElements || 0); })
      .catch(() => { /* same */ });

    return () => { cancelled = true; };
    // Depends on `providers` so the strip refreshes whenever the list does -- approving from a
    // row action would otherwise leave a stale count for the rest of the session.
  }, [api, providers]);

  const fetchProviders = useCallback(async () => {
    try {
      setLoading(true);
      const activeFilters = {};
      if (filters.name) activeFilters.name = filters.name;
      if (filters.countryId) activeFilters.countryId = filters.countryId;
      if (filters.cityId) activeFilters.cityId = filters.cityId;
      if (filters.serviceType) activeFilters.serviceType = filters.serviceType;
      if (filters.providerType) activeFilters.providerType = filters.providerType;
      if (filters.intakeSource) activeFilters.intakeSource = filters.intakeSource;
      if (filters.visibility) activeFilters.visibility = filters.visibility;
      // '' means all; 'false' (unclaimed) is meaningful, so this can't be a truthiness check.
      if (filters.claimed !== '') activeFilters.claimed = filters.claimed;
      const state = filters.state || null;
      const data = await providersApi.getAllProvidersPaginated(
        page, rowsPerPage, sort, direction, state, activeFilters,
      );
      setProviders(data.content);
      setTotalElements(data.totalElements);
      setError('');
    } catch (err) {
      // Surface the backend's message. ProviderSortKeys.orderBy throws a precise allowlist error
      // ("Unknown sort key: 'foo' (allowed: ...)"), and swallowing it leaves someone who
      // hand-edited a bookmarked URL staring at an empty table with no way to tell what is wrong.
      setError(err.backendMessage || 'Failed to load providers. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters, sort, direction]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const handleResetFilters = () => setSearchParams(new URLSearchParams());

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const showError = (msg) => {
    setError(msg);
    setTimeout(() => setError(''), 5000);
  };

  const handleSaveProvider = async () => {
    try {
      setLoading(true);
      if (currentProvider) {
        // Deliberately the admin (partial) endpoint, not PUT /providers/{id}: that one takes a
        // whole ProviderDto and clears images + services from its empty defaults, so this
        // four-field dialog used to wipe both every time it saved.
        await providersApi.adminUpdateProvider(currentProvider.id, {
          title: formData.title,
          about: formData.about,
          instagramUser: formData.instagramUser,
          documentUrl: formData.documentUrl
        });
        await fetchProviders();
        showSuccess(`Provider "${formData.title}" has been updated.`);
      }
    } catch (err) {
      showError(`Failed to update provider: ${err.message}`);
    } finally {
      setLoading(false);
      setOpenDialog(false);
      setCurrentProvider(null);
    }
  };

  const handleDeleteProvider = async () => {
    try {
      setLoading(true);
      await providersApi.deleteProvider(providerToDelete.id);
      await fetchProviders();
      showSuccess(`Provider "${providerToDelete.title}" has been deleted.`);
    } catch (err) {
      // 409 CONFLICT means the provider has recorded Provider Terms acceptances, which are
      // contractual evidence behind RESTRICT foreign keys. That is a refusal the admin can act
      // on, not a fault, so show the guidance instead of a raw status line.
      if (err.status === 409) {
        showError(
          `${err.backendMessage || 'This provider cannot be deleted.'} `
          + 'Open the provider and set its state to DELETED to anonymize instead.',
        );
      } else {
        showError(`Failed to delete provider: ${err.message}`);
      }
    } finally {
      setLoading(false);
      setDeleteConfirmOpen(false);
      setProviderToDelete(null);
    }
  };

  const handleApproveProvider = async (e, providerId) => {
    e.stopPropagation();
    try {
      await providersApi.approveProvider(providerId);
      await fetchProviders();
      showSuccess('Provider has been approved.');
    } catch (err) {
      showError(`Failed to approve provider: ${err.message}`);
    }
  };

  const handleDeclineProvider = async (e, providerId) => {
    e.stopPropagation();
    try {
      await providersApi.declineProvider(providerId);
      await fetchProviders();
      showSuccess('Provider has been declined.');
    } catch (err) {
      showError(`Failed to decline provider: ${err.message}`);
    }
  };

  const handleSuspendProvider = async (e, providerId, currentlySuspended) => {
    e.stopPropagation();
    try {
      if (currentlySuspended) {
        await providersApi.unsuspendProvider(providerId);
        showSuccess('Provider has been unsuspended.');
      } else {
        await providersApi.suspendProvider(providerId);
        showSuccess('Provider has been suspended.');
      }
      await fetchProviders();
    } catch (err) {
      showError(`Failed to update provider suspension: ${err.message}`);
    }
  };

  const getAverageRating = (reviews) => {
    if (!reviews || reviews.length === 0) return 0;
    return reviews.reduce((total, r) => total + r.rating, 0) / reviews.length;
  };

  if (loading && providers.length === 0) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard')} sx={{ mb: 2 }}>
        Dashboard
      </Button>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" component="h1">
          Service Providers Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={() => navigate('/provider-visibility')}
        >
          Manage Visibility Settings
        </Button>
      </Box>

      <MobileProOnboardingToggle />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

      {/* Submissions awaiting a decision that is taken in Submissions, not here.
          NOT "stages with no provider row": PROVIDER_PUBLIC_APPLICATION is raised from two
          places -- a real portal signup (ProviderApplicationService) and
          ClaimInitiationService.requestManualClaim, the no-email fallback for a seeded provider
          that IS already in the table below. The counts are honest about what they are; the
          labels no longer claim otherwise. */}
      {(mobileReview > 0 || portalReview > 0 || awaitingClaim > 0) && (
        <Paper
          variant="outlined"
          sx={{ p: 1.5, mb: 2, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}
        >
          <Typography variant="subtitle2" color="text.secondary">Pipeline</Typography>
          {portalReview > 0 && (
            <Button
              size="small"
              color="warning"
              onClick={() => navigate('/submissions?tab=PROVIDER_PUBLIC_APPLICATION')}
            >
              {portalReview} public applications to review
            </Button>
          )}
          {mobileReview > 0 && (
            <Button
              size="small"
              color="warning"
              onClick={() => navigate('/submissions?tab=PROVIDER_APPLICATION')}
            >
              {mobileReview} in-app applications to review
            </Button>
          )}
          {awaitingClaim > 0 && (
            <Button size="small" onClick={() => navigate('/provider-claims')}>
              {awaitingClaim} invited, awaiting claim
            </Button>
          )}
        </Paper>
      )}

      {/* Search/Filter Bar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Name / Title"
              value={pendingText.name}
              onChange={(e) => setPendingText({ ...pendingText, name: e.target.value })}
              onKeyDown={(e) => e.key === 'Enter' && applyText()}
            />
          </Grid>
          {/* Closed lists from the server, applied immediately like the other selects. */}
          <CountryCityFilter
            countryId={filters.countryId}
            cityId={filters.cityId}
            onChange={setParams}
            loadCities={providersApi.getFilterCities}
            renderItem={(node, key) => (
              <Grid item xs={12} sm={6} md={2} key={key}>{node}</Grid>
            )}
          />
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={filters.state}
                onChange={(e) => setParams({ state: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                {PROVIDER_STATES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Service Type</InputLabel>
              <Select
                label="Service Type"
                value={filters.serviceType}
                onChange={(e) => setParams({ serviceType: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                {SERVICE_TYPES.map(s => <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Provider Type</InputLabel>
              <Select
                label="Provider Type"
                value={filters.providerType}
                onChange={(e) => setParams({ providerType: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                {PROVIDER_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Claim State</InputLabel>
              <Select
                label="Claim State"
                value={filters.claimed}
                onChange={(e) => setParams({ claimed: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Claimed</MenuItem>
                <MenuItem value="false">Not claimed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Origin</InputLabel>
              <Select
                label="Origin"
                value={filters.intakeSource}
                onChange={(e) => setParams({ intakeSource: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                {INTAKE_SOURCES.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Visibility</InputLabel>
              <Select
                label="Visibility"
                value={filters.visibility}
                onChange={(e) => setParams({ visibility: e.target.value })}
              >
                <MenuItem value="">All</MenuItem>
                {VISIBILITIES.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md="auto">
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" onClick={applyText}>Search</Button>
              <Button variant="outlined" onClick={handleResetFilters}>Reset</Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: '70vh' }}>
          <Table stickyHeader aria-label="providers table">
            <TableHead>
              <TableRow>
                {SORTABLE.map(({ key, label }) => (
                  <TableCell key={key} sortDirection={sort === key ? direction.toLowerCase() : false}>
                    <TableSortLabel
                      active={sort === key}
                      direction={sort === key ? direction.toLowerCase() : 'asc'}
                      onClick={() => setParams({
                        sort: key,
                        direction: sort === key && direction === 'ASC' ? 'DESC' : 'ASC',
                        page: null,
                      })}
                    >
                      {label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {providers.map((provider) => (
                <TableRow
                  hover
                  key={provider.id}
                  onClick={() => navigate(`/providers/${provider.id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{provider.id}</TableCell>
                  {/* title alone — this rendered `user?.name || title`, which no single sort key
                      can honestly order. The User column carries the identity instead. */}
                  <TableCell>{provider.title || '-'}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {/* Name AND email, not one or the other -- the Email column was deleted on
                        the basis that it folds in here, so an OR would lose it outright. Falls
                        back to the provider's own public contact for an unclaimed row. */}
                    {provider.user ? (
                      <Link to={`/users/${provider.user.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                        <Box sx={{ fontWeight: 600 }}>{provider.user.name || `#${provider.user.id}`}</Box>
                        {provider.user.email && (
                          <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{provider.user.email}</Box>
                        )}
                      </Link>
                    ) : (
                      <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        {provider.publicEmail || provider.publicPhone || '—'}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={provider.intakeSource || '-'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell><ProviderStatusChips provider={provider} /></TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Rating value={getAverageRating(provider.reviews)} precision={0.5} readOnly size="small" />
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        ({provider.reviews?.length || 0})
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {provider.services?.length > 0 ? (
                      <Chip label={`${provider.services.length} services`} size="small" color="primary" variant="outlined" />
                    ) : '-'}
                  </TableCell>
                  {/* PRO is a chip in the Status cell now; the toggle lives on the details
                      page, where the terms/PayPal warning can sit beside it. */}
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {provider.createdAt ? new Date(provider.createdAt).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Button variant="contained" color="success" size="small"
                        onClick={(e) => handleApproveProvider(e, provider.id)}>
                        Approve
                      </Button>
                      <Button variant="contained" color="error" size="small"
                        onClick={(e) => handleDeclineProvider(e, provider.id)}>
                        Decline
                      </Button>
                      <Tooltip title={provider.adminSuspended ? 'Unsuspend provider' : 'Suspend provider'}>
                        <IconButton
                          size="small"
                          color={provider.adminSuspended ? 'success' : 'error'}
                          onClick={(e) => handleSuspendProvider(e, provider.id, provider.adminSuspended)}
                        >
                          {provider.adminSuspended ? <CheckCircleIcon fontSize="small" /> : <BlockIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 20, 50, 100]}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setParams({ page: newPage || null })}
          onRowsPerPageChange={(e) => setParams({ size: e.target.value, page: null })}
        />
      </Paper>

      {/* Edit Provider Dialog */}
      <Dialog open={openDialog} onClose={() => { setOpenDialog(false); setCurrentProvider(null); }} maxWidth="md" fullWidth>
        <DialogTitle>Edit Provider</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField fullWidth margin="normal" label="Title" name="title" value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })} required />
            <TextField fullWidth margin="normal" label="About" name="about" value={formData.about}
              onChange={(e) => setFormData({ ...formData, about: e.target.value })} multiline rows={3} />
            <TextField fullWidth margin="normal" label="Instagram Username" name="instagramUser" value={formData.instagramUser}
              onChange={(e) => setFormData({ ...formData, instagramUser: e.target.value })}
              InputProps={{ startAdornment: <InstagramIcon color="action" sx={{ mr: 1 }} /> }} />
            <TextField fullWidth margin="normal" label="Document URL" name="documentUrl" value={formData.documentUrl}
              onChange={(e) => setFormData({ ...formData, documentUrl: e.target.value })}
              helperText="URL to certification or license document" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDialog(false); setCurrentProvider(null); }}>Cancel</Button>
          <Button onClick={handleSaveProvider} variant="contained" color="primary">Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => { setDeleteConfirmOpen(false); setProviderToDelete(null); }}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the provider "{providerToDelete?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteConfirmOpen(false); setProviderToDelete(null); }}>Cancel</Button>
          <Button onClick={handleDeleteProvider} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Service Providers Management - Connected to backend API
        </Typography>
      </Box>
    </Container>
  );
};

export default Providers;
